import router, { NextRouter } from 'next/router';

import { SuggestionType } from 'src/components/searchBox/SearchTypes';
import { triggerBingAnalytics } from 'src/services/apiRequests/commerce/bingAnalytics';
import { ICommerceAsset } from 'src/services/responseTypes/commerceResponse';
import { Plan } from 'src/services/responseTypes/displayPlansResponse';
import { PlansMetaData } from 'src/services/responseTypes/plansMetaDataResponse';
import SimpleStore from 'src/store/SimpleStore';
import { Result } from 'src/types/common';
import { PlanCategory } from 'src/types/subscription';

import { getPageName, mixpanelObject } from './analytics';
import { MIXPANEL_ZEE5_COUNTRY } from './analytics/mixpanel/mixpanelConstants';
import {
  shouldOptOutOfPII,
  optingOutOfPII
} from './analytics/mixpanel/mixpanelEventsHelper';
import { getBrowserType } from './browser-type';
import {
  getLocalStorage,
  getSessionStorage,
  setCookie,
  setSessionStorage
} from './browserStorageHandler';
import { DynamicObject } from './commonTypes';
import { XACCESS_TOKEN_URL } from './constants/ApiConstants';
import { XACCESS_AUTHORIZATION_COOKIE } from './constants/cookieConstants';
import {
  HTTP_METHODS,
  HTTP_URL_PARAMS
} from './constants/HttpMethodsConstants';
import {
  COMMERCE_WISHLISTED_PRODUCTS,
  EXPIRES_IN,
  FIRST_PAGE_NAME,
  GUEST_USER_REG_OBJ,
  USER_TOKEN
} from './constants/localStorageConstants';
import {
  AS_ZEE5_COM,
  CountryCode,
  DisplayLanguage,
  PartnerPlatforms,
  WEB_DEVICE_CHROME_AUDIO_TECHNOLOGY,
  WEB_DEVICE_SAFARI_AUDIO_TECHNOLOGY,
  WEB_DEVICE_VIDEO_CODEC,
  WEB_DEVICE_VIDEO_RANGE
} from './constants/stringConstants';
import {
  XACCESS_AUTHORIZATION_COOKIE_EXPIRY,
  convivaCustomConstants
} from './constants/utilConstants';
import { deviceDetector } from './deviceDetector';
import { getPlatformTokenUrl } from './UrlGenerator';
import { httpService } from '../services/fetch/HttpService';

let appsflyerEventQueue: any[] = [];

/* Check for device authentication page for mobile and tv users */
export const mobileCheckForDeviceAuth = () =>
  window &&
  window.location.pathname.match(/\/device|\/authenticatedevice.html/i) &&
  window.navigator.userAgent.match(/iPhone|iPod|Android|tv/i);

// Format is dd/mm/yyyy
export const getUsersAge = (dob: string | undefined) => {
  if (dob) {
    let dobDate: Date;
    // Check if the date format is "YYYY-MM-DD"
    if (/^\d{4}-\d{2}-\d{2}$/.test(dob)) {
      dobDate = new Date(dob);
    }
    // Check if the date format is "DD-MM-YYYY"
    else if (/^\d{2}-\d{2}-\d{4}$/.test(dob)) {
      const [day, month, year] = dob.split('-');
      dobDate = new Date(Number(year), Number(month) - 1, Number(day));
    }
    // Assume the default format is "DD/MM/YYYY"
    else {
      const [day, month, year] = dob.split('/');
      dobDate = new Date(Number(year), Number(month) - 1, Number(day));
    }
    // Calculate the age
    const age = new Date().getFullYear() - dobDate.getFullYear();
    return age;
  }
  return '';
};

export const campaignParams = () => {
  const campaign_keywords = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_content',
      'utm_term',
      'is_retargeting',
      'pid',
      'c'
    ],
    params: { [key: string]: unknown } = {};
  // first_params = {};
  let kw = '';
  let index;
  for (index = 0; index < campaign_keywords.length; ++index) {
    kw = getQueryParam(document.URL, campaign_keywords[index]);
    if (kw.length) {
      if (campaign_keywords[index] === 'is_retargeting') {
        params['utm_retargeting_flag' + ' [last touch]'] = kw;
      } else if (campaign_keywords[index] === 'pid') {
        params['utm_source' + ' [last touch]'] = kw;
      } else if (campaign_keywords[index] === 'c') {
        params['utm_campaign' + ' [last touch]'] = kw;
      } else {
        params[campaign_keywords[index] + ' [last touch]'] = kw;
      }
    } else {
      params[campaign_keywords[index] + ' [last touch]'] = '';
    }
  }
  const UTMParams = getUtmParams();

  if (!params['utm_campaign [last touch]'] && UTMParams.utm_campaign) {
    params['utm_campaign [last touch]'] = UTMParams.utm_campaign;
  }

  if (!params['utm_content [last touch]'] && UTMParams.utm_content) {
    params['utm_content [last touch]'] = UTMParams.utm_content;
  }

  if (!params['utm_medium [last touch]'] && UTMParams.utm_medium) {
    params['utm_medium [last touch]'] = UTMParams.utm_medium;
  }

  if (!params['utm_source [last touch]'] && UTMParams.utm_source) {
    params['utm_source [last touch]'] = UTMParams.utm_source;
  }

  if (!params['utm_term [last touch]'] && UTMParams.utm_term) {
    params['utm_term [last touch]'] = UTMParams.utm_term;
  }

  window.mixpanel.register(params);
};

/* eslint-disable */
export const getQueryParam = (url: string, param: string) => {
  // Expects a raw URL
  param = param.replace(/[[]/, '[').replace(/[]]/, ']');
  var regexS = '[?&]' + param + '=([^&#]*)',
    regex = new RegExp(regexS),
    results: any = regex.exec(url);
  if (
    results === null ||
    (results && typeof results[1] !== 'string' && results[1].length)
  ) {
    return '';
  } else {
    return decodeURIComponent(results[1]).replace(/\W/gi, ' ');
  }
};

/* Global URL Generator Helper Function */
export const globalUrlHelper = (url: string, isTruncate = false) => {
  let finalUrl = url;
  if (!isServer()) {
    const countryCode = SimpleStore.get(HTTP_URL_PARAMS.COUNTRY_CODE);
    const displayLang = SimpleStore.get(HTTP_URL_PARAMS.DISPLAY_LANGUAGE);
    const isGlobal = countryCode && countryCode !== CountryCode.IN;

    if (finalUrl && isGlobal) {
      finalUrl = isTruncate
        ? url && displayLang === DisplayLanguage.ENGLISH
          ? url.replace(`/global`, '')
          : url.replace(`/${displayLang}/global`, '')
        : displayLang === DisplayLanguage.ENGLISH
        ? `/global${url}`
        : `/${displayLang}/global${url}`;
      if (finalUrl !== '/' && finalUrl[finalUrl.length - 1] === '/') {
        finalUrl = finalUrl.substring(0, finalUrl.length - 1);
      }
    }
  }
  return finalUrl;
};

export const getSearchParamsValue = (
  query = window.location.search,
  key: string
) => {
  if (query[0] === '?') {
    query = query.substring(1);
  }
  const vars = query.split('&');
  for (let i = 0; i < vars.length; i += 1) {
    const pair = vars[i].split('=');
    if (decodeURIComponent(pair[0]) === key) {
      return decodeURIComponent(pair[1]);
    }
  }
  return null;
};

/* extract token from localstorage saved via "/guestUserRegistration" API in new payment
journey from consumption page - in case of guest user */
export const getTempUserBearerToken = () => {
  const tempUserDetails = getLocalStorage(GUEST_USER_REG_OBJ);
  let access_token = '';
  if (tempUserDetails) {
    try {
      ({ access_token = '' } = JSON.parse(tempUserDetails) || {});
    } catch (e) {
      console.error('Error parsing json: ', e);
    }
  }
  return access_token;
};

export const getSelectedSubtitle = () => {
  let selectedSubtitle = convivaCustomConstants.NOT_APPLICABLE;
  try {
    var subsActive =
      window.videoPlayer &&
      window.videoPlayer
        .textTracks()
        .tracks_.filter((subs: { mode: string }) => subs.mode === 'showing');
    if (subsActive && subsActive.length > 0) {
      selectedSubtitle = subsActive[0].label;
    }
  } catch (error) {
    return selectedSubtitle;
  }
  return selectedSubtitle;
};

export const drmSecurityLevel = () => {
  const browserName = getUserAgents() || 'NA';
  return browserName === 'Safari'
    ? convivaCustomConstants.DRM_SECURITY_SAFARI
    : convivaCustomConstants.DRM_SECURITY_OTHER_BROWSERS;
};

export const isSubtitlesAvailable = () => {
  let subtitleAvailable = convivaCustomConstants.NOT_APPLICABLE;
  try {
    var subsActive = window.videoPlayer && window.videoPlayer.textTracks();
    if (subsActive && subsActive.length === 0) {
      subtitleAvailable = convivaCustomConstants.NOT_APPLICABLE;
    } else if (subsActive && subsActive.length >= 1) {
      if (
        subsActive.length === 1 &&
        subsActive[0].label === 'segment-metadata'
      ) {
        subtitleAvailable = convivaCustomConstants.NOT_APPLICABLE;
      } else {
        subtitleAvailable =
          getSelectedSubtitle() === convivaCustomConstants.NOT_APPLICABLE
            ? convivaCustomConstants.OFF
            : convivaCustomConstants.ON;
      }
    }
  } catch (error) {
    return subtitleAvailable;
  }
  return subtitleAvailable;
};

const getVideoQualityDisplay = (qualityValue: string | number) => {
  if (qualityValue === 240) {
    return 'Data Saver - 240p';
  } else if (qualityValue === 480) {
    return 'SD - 480p';
  } else if (qualityValue === 720) {
    return 'HD - 720p';
  } else if (qualityValue === 1080) {
    return 'Full HD - 1080p';
  } else {
    return 'Auto-Recommended';
  }
};

export const getVideoQuality = () => {
  let videoQuality = convivaCustomConstants.NOT_RECEIVED;
  try {
    let qualityLevels =
      window.videoPlayer && window.videoPlayer.qualityLevels();
    if (qualityLevels && qualityLevels.length > 0) {
      videoQuality =
        qualityLevels[qualityLevels.selectedIndex].height || videoQuality;
    }
  } catch (error) {
    return getVideoQualityDisplay(videoQuality);
  }
  return getVideoQualityDisplay(videoQuality);
};

export const parseJwt = (token: string) => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => {
        // eslint-disable-next-line
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );

  return JSON.parse(jsonPayload);
};

export const getUserAgent = () => {
  const userAgent = window?.navigator?.userAgent || '';
  if (userAgent.includes('Safari')) {
    window.isSafari = true;
  }
  return userAgent;
};

export const isServer = (): boolean => {
  if (typeof window !== 'undefined' && window && window.document) {
    return false;
  }
  return true;
};

export const isWebView = () => {
  if (
    !isServer() &&
    typeof window !== undefined &&
    window &&
    (window.zee5 ||
      (window.webkit &&
        window.webkit.messageHandlers &&
        window.webkit.messageHandlers.zee5))
  ) {
    return true;
  } else {
    return false;
  }
};

//generates the regex based on the country data
export const numRegex = (
  minLength: number | string,
  maxLength: number | string
) => {
  return `^(\\+\\d{1,3}[- ]?)?\\d{${minLength},${maxLength}}$`;
};

export const debounce = (callback: Function, waitFor: number) => {
  let timeout: ReturnType<typeof setTimeout>;
  return (
    query: string,
    setSuggestionResult: (suggestionResult: Array<SuggestionType>) => void,
    inMemoryCache: (requestObj: DynamicObject, headers: string[]) => any,
    headers: string[],
    requestObj: DynamicObject
  ) => {
    timeout && clearTimeout(timeout);
    timeout = setTimeout(() => {
      callback(query, setSuggestionResult, inMemoryCache, headers, requestObj);
    }, waitFor);
  };
};

export const getCurrentTimeStamp = () => {
  return Math.floor(Math.floor(new Date().getTime() / 1000));
};

/* extract expires_in from localstorage saved via "/guestUserRegistration" API in new payment
journey from consumption page - in case of guest user */
export const getTempUserExpiresIn = () => {
  const tempUserDetails = getLocalStorage(GUEST_USER_REG_OBJ);
  const token = getLocalStorage(USER_TOKEN);
  let expiresIn: number = 0;
  if (tempUserDetails && !token) {
    try {
      expiresIn = JSON.parse(tempUserDetails)?.EXPIRES_IN || 0;
    } catch (e) {
      console.error('Error parsing json: ', e);
    }
  } else if (token) {
    expiresIn = getLocalStorage(EXPIRES_IN) || 0;
  }
  return expiresIn;
};

const getChromeDdToken = () => {
  return JSON.stringify({
    schema_version: '1',
    os_name: 'N/A',
    os_version: 'N/A',
    platform_name: 'Chrome',
    platform_version: '104',
    device_name: '',
    app_name: 'Web',
    app_version: '2.52.31',
    player_capabilities: {
      audio_channel: ['STEREO'],
      video_codec: ['H264'],
      container: ['MP4', 'TS'],
      package: ['DASH', 'HLS'],
      resolution: ['240p', 'SD', 'HD', 'FHD'],
      dynamic_range: ['SDR']
    },
    security_capabilities: {
      encryption: ['WIDEVINE_AES_CTR'],
      widevine_security_level: ['L3'],
      hdcp_version: ['HDCP_V1', 'HDCP_V2', 'HDCP_V2_1', 'HDCP_V2_2']
    }
  });
};
const getSafariDdToken = () => {
  return JSON.stringify({
    schema_version: '1',
    os_name: 'N/A',
    os_version: 'N/A',
    platform_name: 'Safari',
    platform_version: '15.4',
    device_name: '',
    app_name: 'Web',
    app_version: '2.52.31',
    player_capabilities: {
      audio_channel: ['STEREO', 'DOLBY'],
      video_codec: ['H264'],
      container: ['MP4', 'TS'],
      package: ['HLS'],
      resolution: ['240p', 'SD', 'HD', 'FHD'],
      dynamic_range: ['SDR']
    },
    security_capabilities: {
      encryption: ['FAIRPLAY_AES_CBCS'],
      hdcp_version: ['HDCP_V1', 'HDCP_V2', 'HDCP_V2_1', 'HDCP_V2_2']
    }
  });
};

export const getSpapiDdToken = () => {
  if (getBrowserType() === 'Safari') return btoa(getSafariDdToken());
  return btoa(getChromeDdToken());
};

export const checkKidsCard = (genres: DynamicObject) => {
  let isKidCard = false;
  let isEduaraCard = false;
  if (genres) {
    genres.forEach((ele: DynamicObject) => {
      const { id = '' } = ele || {};
      if (id === 'Kids' || id === 'Kid') {
        isKidCard = true;
      } else if (id === 'Education') {
        isEduaraCard = true;
      }
    });
  }
  return isKidCard && !isEduaraCard;
};

export const checkIfEduauraa = (episodeData: any) => {
  if (
    episodeData &&
    episodeData.tags &&
    episodeData.tags.find((item: string) => item === 'Eduauraa')
  ) {
    return true;
  }
  return false;
};

/**
 * Disables background scrolling for modal
 */
export const disableBackgroundScrolling = () => {
  document.body.style.overflow = 'hidden';
};

/**
 * Enables background scrolling for modal
 */
export const enableBackgroundScrolling = () => {
  document.body.style.overflow = 'auto';
};

export const pausePlayerPopup = () => {
  if (window?.videoPlayer && window?.videoPlayer.src()) {
    window.videoPlayer.pause();
  }
};

export const resumePlayerPopup = () => {
  if (
    window &&
    window?.videoPlayer &&
    window?.videoPlayer?.src() &&
    window?.videoPlayer?.paused()
  ) {
    try {
      window?.videoPlayer?.play();
    } catch (error) {
      console.error('Error in playing player', error);
    }
  }
};

//checking if the url starts with as
export const isAsZee5com = () => {
  return (
    !isServer() &&
    window &&
    window.location &&
    window.location.host === AS_ZEE5_COM
  );
};

export const parseJsonObject = (data: string | any) => {
  let parsedData = {};
  try {
    if (typeof data !== 'object' && data !== PartnerPlatforms.TATASKY_PARTNER) {
      parsedData = data && JSON.parse(data);
      return parsedData;
    }
    return data;
  } catch (e) {
    console.error(e);
  }
  return data;
};
// to filter out the tappp plan selected by the user
export const getTappPlan = (
  planId: string,
  arr: Plan[],
  plansMetaData: PlansMetaData[]
) => {
  if (arr.length && arr.length > 0) {
    const selectedPlan = arr.filter((plan: Plan) => plan.id === planId);
    const selectedPlanMetadata = plansMetaData.find(
      (plan: PlansMetaData) => plan.id === selectedPlan[0].id
    );
    let formattedPlan = { ...selectedPlan[0], ...selectedPlanMetadata };
    formattedPlan.all_paymentProviders = formattedPlan.payment_providers;
    return { ...formattedPlan };
  }
};

export const getTappPlanNew = (planId: string, plans: PlanCategory[]) => {
  const selectedPlan = plans
    .flatMap((plan) => plan.options)
    .find((option) => option.id === planId);
  if (selectedPlan) {
    const { price, duration } = selectedPlan;
    return {
      ...selectedPlan,
      all_paymentProviders: selectedPlan.payment_providers,
      actual_price: price?.actual,
      price: price?.actual,
      currency: price?.currency,
      billingFrequency: duration?.frequency,
      duration: duration?.label,
      duration_text: price?.market,
      purchaseAllowed: !!selectedPlan?.purchaseAllowed
    };
  }
  return null;
};

// app-flyer
export const trackEventsOnAppsflyer = (eventObj: any) => {
  if (!isServer() && window && window.AF) {
    window.AF('pba', 'event', eventObj);
  }
};

export const appsflyerEventPush = (trackData: any) => {
  if (shouldOptOutOfPII()) {
    const objWithoutPII = optingOutOfPII();
    trackData = { ...trackData, ...objWithoutPII };
  }
  if (typeof window !== 'undefined' && window.AF && trackData) {
    if (appsflyerEventQueue && appsflyerEventQueue.length) {
      appsflyerEventQueue.forEach((item) => {
        trackEventsOnAppsflyer(item.trackData);
      });
      appsflyerEventQueue = [];
    }
    trackEventsOnAppsflyer(trackData);
  } else if (trackData) {
    appsflyerEventQueue.push({ trackData });
    if (typeof window !== 'undefined') {
      // Appsflyer event will not publish if not intialised within 7 seconds
      setTimeout(() => {
        if (window.AF) {
          trackEventsOnAppsflyer(trackData);
        }
      }, 7 * 1000);
    }
  }
};

export const getLatestSubscription = (subscription: any, date: any) => {
  let latestSubscription: any = {};
  latestSubscription =
    subscription && subscription.length
      ? subscription.sort((item1: any, item2: any) => {
          // sorting in decending order of the date
          if (Date.parse(item1[date]) < Date.parse(item2[date])) {
            return 1;
          }
          if (Date.parse(item1[date]) > Date.parse(item2[date])) {
            return -1;
          }
          return 0;
        })
      : [];
  return latestSubscription[0];
};

export const getPlatformToken = async () => {
  const platformTokenUrl = getPlatformTokenUrl(XACCESS_TOKEN_URL);
  const response = await httpService.fetchApiData({
    url: platformTokenUrl,
    type: HTTP_METHODS.GET
  });
  setCookie(
    XACCESS_AUTHORIZATION_COOKIE,
    response.token,
    XACCESS_AUTHORIZATION_COOKIE_EXPIRY
  );
};

// return url parameters as an object
export const getUTMParamsAsObject = (url: string) => {
  let queryStartIndex = 0;
  if (((url && decodeURI(url).indexOf('?')) as any) > -1) {
    queryStartIndex = decodeURI(url).indexOf('?') + 1;
  }

  let queryParams = url.split('&');
  queryParams = queryParams.filter((item) => !!item);
  url = queryParams.join('&');

  if (url && url.indexOf('utm_') > -1) {
    return JSON.parse(
      `{"${decodeURI(url)
        .substring(queryStartIndex)
        .replace(/"/g, '\\"')
        .replace(/&/g, '","')
        .replace(/=/g, '":"')}"}`
    );
  }
  return {};
};

export const getDeviceResolution = () => {
  let height = 'NA';
  let width = 'NA';
  try {
    height =
      window && window.screen && window.screen.availHeight
        ? window.screen.availHeight.toString()
        : height;
    width =
      window && window.screen && window.screen.availWidth
        ? window.screen.availWidth.toString()
        : width;
  } catch (error) {
    console.error('error in getting device resolution: ', error);
  }
  return `${height} * ${width}`;
};

export const getDeviceAudioTechnology = () => {
  const deviceBrowser = getDeviceInfo().DeviceBrowser;
  if (deviceBrowser && deviceBrowser.toLowerCase() === 'chrome') {
    return WEB_DEVICE_CHROME_AUDIO_TECHNOLOGY;
  } else if (deviceBrowser && deviceBrowser.toLowerCase() === 'safari') {
    return WEB_DEVICE_SAFARI_AUDIO_TECHNOLOGY;
  } else {
    return WEB_DEVICE_CHROME_AUDIO_TECHNOLOGY;
  }
};

export const getDeviceVideoCodec = () => {
  return WEB_DEVICE_VIDEO_CODEC;
};

export const getDeviceVideoRange = () => {
  return WEB_DEVICE_VIDEO_RANGE;
};

export const languageMapping = (lang: any) => {
  switch (lang) {
    case 'en':
      return 'English';
    case 'hi':
      return 'Hindi';
    case 'mr':
      return 'Marathi';
    case 'te':
      return 'Telugu';
    case 'kn':
      return 'Kannada';
    case 'ta':
      return 'Tamil';
    case 'ml':
      return 'Malayalam';
    case 'bn':
      return 'Bengali';
    case 'gu':
      return 'Gujarati';
    case 'ur':
      return 'Urdu';
    case 'pa':
      return 'Punjabi';
    case 'hr':
      return 'Bhojpuri';
    case 'or':
      return 'Odia';
    case 'de':
      return 'German';
    case 'th':
      return 'Thai';
    case 'id':
      return 'Indonesian';
    case 'in':
      return 'Indonesian';
    case 'ms':
      return 'Malay';
    case 'ru':
      return 'Russian';
    case 'zho':
      return 'Chinese';
    case 'es':
      return 'Spanish';
    case 'ja':
      return 'Japanese';
    case 'fr':
      return 'French';
    case 'Off':
      return 'Off';
    default:
      return lang;
  }
};

export const getDeviceInfo = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  const deviceInfo = {} as any;
  if (
    userAgent.match(/android/i) ||
    userAgent.match(/webos/i) ||
    userAgent.match(/iphone/i) ||
    userAgent.match(/ipad/i) ||
    userAgent.match(/ipod/i) ||
    userAgent.match(/blackberry/i) ||
    userAgent.match(/windows phone/i)
  ) {
    deviceInfo.isMobile = true;
    if (
      /ipad|xoom|sch-i800|playbook|tablet|kindle/i.test(userAgent.toLowerCase())
    ) {
      deviceInfo.DeviceType = 'Tablet';
    } else {
      deviceInfo.DeviceType = 'Mobile';
    }
  } else {
    deviceInfo.DeviceType = 'Desktop';
    deviceInfo.isMobile = false;
  }
  if (
    deviceInfo.DeviceType === 'Mobile' ||
    deviceInfo.DeviceType === 'Tablet'
  ) {
    try {
      deviceInfo.DeviceBrowser = getUserAgents();
    } catch (err) {
      console.error('Error while device identification: ', err);
    }
  }
  return deviceInfo;
};

const getUserAgents = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  let DeviceBrowser = '';
  // to detect Android/Mweb/Chrome
  if (
    userAgent.indexOf('chrome') !== -1 &&
    userAgent.indexOf('safari') !== -1
  ) {
    DeviceBrowser = 'Chrome';
    // to detect IOS/Mweb/Chrome
  } else if (
    userAgent.indexOf('crios') !== -1 &&
    userAgent.indexOf('safari') !== -1
  ) {
    DeviceBrowser = 'Safari';
    // case only crios/chrome comes in user agent
  } else if (
    userAgent.indexOf('crios') !== -1 ||
    userAgent.indexOf('chrome') !== -1
  ) {
    DeviceBrowser = 'Chrome';
  } else if (userAgent.indexOf('vivobrowser') !== -1) {
    DeviceBrowser = 'vivo';
  } else if (userAgent.indexOf('ucbrowser') !== -1) {
    DeviceBrowser = 'UCbrowser';
  } else if (userAgent.indexOf('safari') !== -1) {
    DeviceBrowser = 'Safari';
  } else if (userAgent.match('fxios') || userAgent.match('firefox')) {
    DeviceBrowser = 'FireFox';
  } else if (userAgent.indexOf('opera mini') !== -1) {
    DeviceBrowser = 'Opera Mini';
  } else if (userAgent.indexOf('opera') !== -1) {
    DeviceBrowser = 'opera';
  } else {
    DeviceBrowser = 'Unknown';
  }
  return DeviceBrowser;
};

// convert seconds to human readable format
export function convertTimeToHumanReadableFormat(seconds: number) {
  var hours = Math.floor(seconds / 3600);
  var minutes = Math.floor((seconds - hours * 3600) / 60);
  var seconds = seconds - hours * 3600 - minutes * 60;
  if (!!hours) {
    if (!!minutes) {
      return `${hours} hr ${minutes} min`;
    } else {
      return `${hours} hr`;
    }
  }
  if (!!minutes) {
    return `${minutes} min`;
  }
  return `${seconds} sec`;
}

// generate random Integers between 2 values inclusive of min & max
export const generateRandomInteger = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1)) + min || 0;

// get TrueCaller Subscription flow for A/B testing.
export const getVariantForABTestingTC = () => {
  const randomNumber = generateRandomInteger(1, 100);
  const successTestSet = [1]; // to target 10% audience on random
  // experiment is ON only when there random generated number is present in success test set.
  const variant = successTestSet.includes(randomNumber) ? true : false;
  return variant;
};

export const urlCorrector = (
  actualTitle: string,
  titleFromRouter: string,
  pathName: string,
  displayLocale: string,
  seoUrlStructure: string
) => {
  let locale = '';
  if (displayLocale !== DisplayLanguage.ENGLISH) {
    locale = `/${displayLocale}`;
  }
  if (seoUrlStructure) {
    return `${locale}${seoUrlStructure}`;
  }
  if (actualTitle && titleFromRouter && pathName) {
    const finalURL = `${locale}${pathName.replace(
      titleFromRouter,
      actualTitle
    )}`;
    return finalURL;
  }
  return '';
};

export const setLandingPage = (country = 'N/A') => {
  if (!getSessionStorage(FIRST_PAGE_NAME)) {
    const firstPageName = getPageName(router.asPath);
    if (typeof firstPageName !== 'undefined') {
      setSessionStorage(FIRST_PAGE_NAME, firstPageName);
    }
  }
  mixpanelObject.setProperty(MIXPANEL_ZEE5_COUNTRY, country.toUpperCase());
};

const rgxMapper = function (ua: string, arrays: string | any[]) {
  var i = 0,
    j,
    k,
    p,
    q,
    matches,
    match;
  const finalObj = {};
  const OBJ_TYPE = 'object';
  const FUNC_TYPE = 'function';
  var props;
  // loop through all regexes maps
  while (i < arrays.length && !matches) {
    var regex = arrays[i], // even sequence (0,2,4,..)
      props = arrays[i + 1];
    // odd sequence (1,3,5,..)
    j = k = 0;
    // try matching uastring with regexes
    while (j < regex.length && !matches) {
      if (!regex[j]) {
        break;
      }
      matches = regex[j++]?.exec(ua);
      if (!!matches) {
        for (p = 0; p < props.length; p++) {
          match = matches[++k];
          q = props[p];
          if (typeof q === 'string') {
            (finalObj as any)[q?.toUpperCase()] = match;
          }
          if (q && q?.length && typeof q?.[0] === 'string') {
            (finalObj as any)[q[0]?.toUpperCase()] = q[1];
          }
        }
      }
    }
    i += 2;
  }

  return finalObj;
};

export const getDevice = (ua: string) => {
  let device: {
    VENDOR: string;
    MODEL: string;
    TYPE: string;
  } = {
    VENDOR: '',
    MODEL: '',
    TYPE: ''
  };
  const navigator1 = window.navigator;
  const value: any = rgxMapper(ua, deviceDetector());
  device.VENDOR = value?.VENDOR || '';
  device.MODEL = value?.MODEL || '';
  device.TYPE = value?.TYPE || '';
  if (
    ua &&
    device['MODEL'] == 'Macintosh' &&
    navigator1 &&
    typeof (navigator1 as any).standalone !==
      convivaCustomConstants.UNDEF_TYPE &&
    navigator1.maxTouchPoints &&
    navigator1.maxTouchPoints > 2
  ) {
    device['MODEL'] = 'iPad';
    device['TYPE'] = convivaCustomConstants.TABLET;
  }
  return device;
};
export const getCommerceWishlistedProductsKeys = () => {
  const wishlistedProducts =
    getSessionStorage(COMMERCE_WISHLISTED_PRODUCTS) || [];
  return wishlistedProducts.map((product: ICommerceAsset) => product?.id);
};

export const setCommerceWishlistData = (newProduct: ICommerceAsset) => {
  const wishlistedProducts =
    getSessionStorage(COMMERCE_WISHLISTED_PRODUCTS) || {};
  let newWishlistedProducts = [];
  if (Object.keys(wishlistedProducts).length) {
    newWishlistedProducts = [...wishlistedProducts, newProduct];
  } else {
    newWishlistedProducts = [newProduct];
  }
  setSessionStorage(COMMERCE_WISHLISTED_PRODUCTS, newWishlistedProducts);
};

export const removeCommerceWishlistedProduct = (id: string) => {
  const wishlistedProducts =
    getSessionStorage(COMMERCE_WISHLISTED_PRODUCTS) || {};
  let newWishlistedProducts = [];
  if (Object.keys(wishlistedProducts).length) {
    newWishlistedProducts = wishlistedProducts.filter(
      (product: ICommerceAsset) => product?.id !== id
    );
  }
  setSessionStorage(COMMERCE_WISHLISTED_PRODUCTS, newWishlistedProducts);
};

export const intersectionCallback = (entries: IntersectionObserverEntry[]) => {
  entries.forEach((entry: any) => {
    if (entry?.isIntersecting) {
      const { visibilityfeedbackurl = '' } = entry.target?.dataset || {};
      if (visibilityfeedbackurl) {
        // Call visibilityFeedbackUrl if card intersects
        triggerBingAnalytics(visibilityfeedbackurl);
      }
    }
  });
};

export const storeUtmData = (router: NextRouter) => {
  const utmParams = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term'
  ];

  utmParams.forEach((param) => {
    const value = router.query[param];
    if (value) {
      setSessionStorage(param, String(value));
    }
  });
};

interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

export const getUtmParams = () => {
  const utmParams: UtmParams = {};

  const keys: (keyof UtmParams)[] = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term'
  ];

  keys.forEach((key) => {
    const value = getSessionStorage(key);
    if (value) {
      utmParams[key] = value;
    }
  });

  return utmParams;
};

export function tryCatch<T, E = Error>(fn: () => T): Result<T, E> {
  try {
    const data = fn();
    return { data, error: null };
  } catch (error) {
    return { data: null, error: error as E };
  }
}
